import { ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { DashboardPage } from './pages/DashboardPage';
import { InvitePage } from './pages/InvitePage';
import { CareHomeRegistrationPage } from './pages/CareHomeRegistrationPage';
import { StaffInvitePage } from './pages/StaffInvitePage';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { MessagingPage } from './pages/MessagingPage';
import { MessagingProvider } from './contexts/MessagingContext';
import { LandingPage } from './pages/LandingPage';
import { ProfilePage } from './pages/ProfilePage';
import { PeoplePage } from './pages/PeoplePage';
import { SettingsPage } from './pages/SettingsPage';
import { DependentSettingsPage } from './pages/DependentSettingsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { PricingPage } from './pages/PricingPage';
import { RoadmapPage } from './pages/RoadmapPage';
import { ChecklistsPage } from './pages/ChecklistsPage';
import { ChecklistDetailPage } from './pages/ChecklistDetailPage';
import { CalendarPage } from './pages/CalendarPage';
import { JournalPage } from './pages/JournalPage';
import { EmergencyCardPage } from './pages/EmergencyCardPage';
import { GpSummaryPage } from './pages/GpSummaryPage';
import { HomePage } from './pages/HomePage';
import { CookieConsent } from './components/CookieConsent';
import { AppLayout } from './components/AppLayout';

const isDevAuth = import.meta.env.VITE_AUTH_MODE === 'dev';

// In dev mode: import DevAuthProvider directly (no Clerk dependency)
// In prod mode: lazy-load ClerkAuthProvider to avoid Clerk init in dev
const DevAuthProviderModule = isDevAuth
  ? await import('./contexts/DevAuthContext')
  : null;

const ClerkAuthProviderModule = !isDevAuth
  ? await import('./contexts/ClerkAuthContext')
  : null;

const ClerkModule = !isDevAuth
  ? await import('@clerk/clerk-react')
  : null;

function AuthProvider({ children }: { children: ReactNode }) {
  if (isDevAuth && DevAuthProviderModule) {
    return <DevAuthProviderModule.DevAuthProvider>{children}</DevAuthProviderModule.DevAuthProvider>;
  }
  if (ClerkAuthProviderModule) {
    return <ClerkAuthProviderModule.ClerkAuthProvider>{children}</ClerkAuthProviderModule.ClerkAuthProvider>;
  }
  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  if (isDevAuth) {
    return <>{children}</>;
  }
  if (ClerkModule) {
    return (
      <>
        <ClerkModule.SignedIn>{children}</ClerkModule.SignedIn>
        <ClerkModule.SignedOut>
          <ClerkModule.RedirectToSignIn />
        </ClerkModule.SignedOut>
      </>
    );
  }
  return <>{children}</>;
}

function RootRoute() {
  if (isDevAuth) {
    return <LandingPage />;
  }
  if (ClerkModule) {
    return (
      <>
        <ClerkModule.SignedIn>
          <Navigate to="/dashboard" replace />
        </ClerkModule.SignedIn>
        <ClerkModule.SignedOut>
          <LandingPage />
        </ClerkModule.SignedOut>
      </>
    );
  }
  return <LandingPage />;
}

export function App() {
  return (
    <AuthProvider>
      <MessagingProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invite/:token"
          element={
            <ProtectedRoute>
              <InvitePage />
            </ProtectedRoute>
          }
        />
        {/* Care home registration — outside AppLayout */}
        <Route
          path="/onboarding/carehome"
          element={
            <ProtectedRoute>
              <CareHomeRegistrationPage />
            </ProtectedRoute>
          }
        />
        {/* Legacy redirects — old standalone care home routes now live in the unified shell */}
        <Route path="/staff/dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="/carehome/:careHomeId/staff" element={<Navigate to="/people" replace />} />
        <Route path="/carehome/:careHomeId/schedules" element={<Navigate to="/people" replace />} />
        <Route
          path="/staff-invite/:token"
          element={
            <ProtectedRoute>
              <StaffInvitePage />
            </ProtectedRoute>
          }
        />
        {/* Unified app shell — WorkspaceProvider determines family vs Pro (care home) */}
        <Route
          element={
            <ProtectedRoute>
              <WorkspaceProvider>
                <AppLayout />
              </WorkspaceProvider>
            </ProtectedRoute>
          }
        >
          <Route path="/home" element={<HomePage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/journal" element={<JournalPage />} />
          <Route path="/messages" element={<MessagingPage />} />
          <Route path="/checklists" element={<ChecklistsPage />} />
          <Route path="/checklists/:checklistId" element={<ChecklistDetailPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/dependents/:id" element={<DependentSettingsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/gp-summary/:dependentId" element={<GpSummaryPage />} />
        </Route>
        {/* Public pages */}
        <Route path="/card/:token" element={<EmergencyCardPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/roadmap" element={<RoadmapPage />} />
        <Route path="/" element={<RootRoute />} />
      </Routes>
      <CookieConsent />
      </MessagingProvider>
    </AuthProvider>
  );
}
